import axios, { AxiosResponse } from 'axios';
import { apiClient } from '../apiClient';
import { DailyRental } from '../dailyReports/dailyRentals';
import { User } from '../users';
import { Site } from '../sites/sites';

export interface Rental {
    crudStatus: number;
    _id: string;
    designation: string;
    codeProC: string;
    email?: string;
    usageCount?: {
        total: number;
        dailyTransfersPlanned: number;
        dailyTransfersRides: number;
        dailyRentals: number;
    };
    displayOrder?: number;
    updatedAt?: string;
    createdAt?: string;
}

export interface RentalsResponse {
    message: string;
    data: Rental[];
}

export interface RentalResponse {
    message: string;
    data: Rental;
}

export interface GetRentalsQueries {
    active?: boolean;
    withUsage?: boolean;
}

export const getAllRentals = async (queries?: GetRentalsQueries): Promise<RentalsResponse> => {
    try {
        const response: AxiosResponse<RentalsResponse> = await apiClient.get('/rental-types', { params: queries });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface UpdateRentalBody {
    designation?: string;
    color?: string;
    crudStatus?: number;
    codeProC?: string;
    email?: string;
    displayOrder?: number | null;
}

export const updateRental = async (rentalId: string, data: UpdateRentalBody): Promise<RentalResponse> => {
    try {
        const response: AxiosResponse<RentalResponse> = await apiClient.patch('/rental-types/' + rentalId, data);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface CreateRentalBody {
    designation: string;
    crudStatus?: number;
    codeProC: string;
    email?: string;
    displayOrder?: number | null;
}

export const createRental = async (data: CreateRentalBody): Promise<RentalResponse> => {
    try {
        const response: AxiosResponse<RentalResponse> = await apiClient.post('/rental-types', data);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export const getRentals = async (): Promise<RentalsResponse> => {
    try {
        const response: AxiosResponse<RentalsResponse> = await apiClient.get('/rental-types');
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface MonthlyRental extends Rental {
    dailyRentalTypesByDate: {
        date: string;
        daily: { dailyRentalType: DailyRental; user: User; site: Site }[];
    }[];
}

export interface GetMonthlyRentalParams {
    date: string;
}

export interface MonthlyRentalsResponse {
    data: MonthlyRental[];
    message: string;
}

export const getMonthlyRentals = async (params: GetMonthlyRentalParams): Promise<MonthlyRentalsResponse> => {
    try {
        const response: AxiosResponse<MonthlyRentalsResponse> = await apiClient.get('/rental-types/monthly/' + params.date);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};
